import React from "react";
import Switch from "@/sot/primitives/Switch.jsx";
import {
  CONSENT_CATEGORIES,
  DEFAULT_CONSENT,
  clearConsentState,
  emitConsentChanged,
  hasConsent,
  readConsentState,
  writeConsentState,
} from "@/lib/consent/getConsent";
import { applyConsentSideEffects } from "@/lib/consent/applyConsent";
import { LanguageContext } from "@/contexts/LanguageContext.jsx";
import {
  getInitialLanguageCode,
  LANGUAGE_CHANGE_EVENT,
  LANGUAGE_STORAGE_KEY,
  normalizeAppLanguageCode,
} from "@/config/languages.js";
import { LANGUAGE_MESSAGES } from "@/i18n/languageMessages.js";
import { LANGUAGE_MESSAGE_PATCHES } from "@/i18n/languageMessagePatches.js";
import { advanceStagingFirstUse } from "@/services/stagingAccessService.js";

const ORDER = [
  { key: "strictly", labelKey: "strictly", bodyKey: "strictlyBody", locked: true },
  { key: "statistics", labelKey: "statistics", bodyKey: "statisticsBody", locked: false },
  { key: "performance", labelKey: "performance", bodyKey: "performanceBody", locked: false },
  { key: "functionality", labelKey: "functionality", bodyKey: "functionalityBody", locked: false },
  { key: "targeting", labelKey: "targeting", bodyKey: "targetingBody", locked: false },
  { key: "social", labelKey: "social", bodyKey: "socialBody", locked: false },
  { key: "beacons", labelKey: "beacons", bodyKey: "beaconsBody", locked: false },
];

function buildExpandedMap() {
  return Object.fromEntries(ORDER.map((item) => [item.key, false]));
}

function deepMergeMessages(base, patch) {
  if (!patch || typeof patch !== "object") return base;
  const output = { ...(base && typeof base === "object" ? base : {}) };

  for (const [key, value] of Object.entries(patch)) {
    const current = output[key];
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      current &&
      typeof current === "object" &&
      !Array.isArray(current)
    ) {
      output[key] = deepMergeMessages(current, value);
      continue;
    }
    output[key] = value;
  }

  return output;
}

function getNestedValue(source, path) {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, segment) => {
      if (!current || typeof current !== "object") return undefined;
      return current[segment];
    }, source);
}

function resolveStandaloneMessages(languageCode = getInitialLanguageCode()) {
  const normalized = normalizeAppLanguageCode(languageCode);
  const short = normalized.split("-")[0];
  const englishBase = LANGUAGE_MESSAGES["en-GB"] || {};
  const englishPatch =
    LANGUAGE_MESSAGE_PATCHES["en-GB"] ||
    LANGUAGE_MESSAGE_PATCHES.en ||
    null;
  const localizedBase =
    normalized === "en-GB"
      ? englishBase
      : LANGUAGE_MESSAGES[normalized] || LANGUAGE_MESSAGES[short] || {};
  const fallbackBase =
    normalized === "en-GB"
      ? englishBase
      : deepMergeMessages(englishBase, localizedBase);
  const fallback = englishPatch
    ? deepMergeMessages(fallbackBase, englishPatch)
    : fallbackBase;
  const patch =
    LANGUAGE_MESSAGE_PATCHES[normalized] ||
    LANGUAGE_MESSAGE_PATCHES[short] ||
    null;
  return patch ? deepMergeMessages(fallback, patch) : fallback;
}

function toChoiceMap(input) {
  const source = input && typeof input === "object" ? input : {};
  const out = { ...DEFAULT_CONSENT };
  for (const key of CONSENT_CATEGORIES) {
    if (key === "strictly") {
      out.strictly = true;
      continue;
    }
    out[key] = !!source[key];
  }
  return out;
}

function shouldDeferInitialPreferences() {
  if (typeof window === "undefined") return false;

  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("consent") === "reset") return false;

    const path = String(url.pathname || "");
    const firstUseCoordinator =
      path.startsWith("/onboarding") && url.searchParams.get("first_use") === "1";
    return (
      path === "/login" ||
      path.startsWith("/auth/callback") ||
      (path.startsWith("/onboarding") && !firstUseCoordinator)
    );
  } catch {
    return false;
  }
}

function advanceFirstUseAfterCookieDecision() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (
      !url.pathname.startsWith("/onboarding") ||
      url.searchParams.get("first_use") !== "1" ||
      (url.searchParams.get("stage") && url.searchParams.get("stage") !== "cookies")
    ) {
      return;
    }
    void advanceStagingFirstUse("category")
      .then(() => {
        const nextUrl = new URL("/onboarding", url.origin);
        nextUrl.searchParams.set("first_use", "1");
        nextUrl.searchParams.set("stage", "category");
        window.history.replaceState({}, "", `${nextUrl.pathname}${nextUrl.search}`);
        window.dispatchEvent(new PopStateEvent("popstate"));
      })
      .catch(() => {
        // Do not advance the visible coordinator if the server-owned stage
        // could not be recorded. A later login safely resumes at Cookies.
      });
  } catch {
    // Consent was still saved; first-use resumption will continue on the next route render.
  }
}

export default function CookieConsent() {
  const language = React.useContext(LanguageContext);
  const [standaloneLanguageCode, setStandaloneLanguageCode] = React.useState(() =>
    normalizeAppLanguageCode(getInitialLanguageCode())
  );
  const standaloneMessages = React.useMemo(
    () =>
      language ? null : resolveStandaloneMessages(standaloneLanguageCode),
    [language, standaloneLanguageCode]
  );
  const t = React.useCallback(
    (key, fallback) => {
      if (language?.t) return language.t(key, fallback);
      const resolved = getNestedValue(standaloneMessages, key);
      return typeof resolved === "string" && resolved.trim() ? resolved : fallback;
    },
    [language, standaloneMessages]
  );
  const [state, setState] = React.useState(() => readConsentState());
  const [open, setOpen] = React.useState(() => {
    const consent = readConsentState();
    return !consent.decided && !shouldDeferInitialPreferences();
  });
  const [expanded, setExpanded] = React.useState(() => buildExpandedMap());

  const prefs = state?.choices || DEFAULT_CONSENT;

  React.useEffect(() => {
    if (language) return undefined;
    if (typeof window === "undefined") return undefined;

    const syncStandaloneLanguage = (nextCode) => {
      setStandaloneLanguageCode(
        normalizeAppLanguageCode(nextCode || getInitialLanguageCode())
      );
    };

    const handleLanguageChange = (event) => {
      syncStandaloneLanguage(event?.detail?.code);
    };

    const handleStorage = (event) => {
      if (event?.key !== LANGUAGE_STORAGE_KEY) return;
      syncStandaloneLanguage(event?.newValue);
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, [language]);

  React.useEffect(() => {
    // Enforce current consent state at boot (e.g., disable analytics when not allowed).
    applyConsentSideEffects(state);
  }, [state]);

  React.useEffect(() => {
    if (state?.decided === true) advanceFirstUseAfterCookieDecision();
  }, [state?.decided]);

  const persistDecision = React.useCallback(
    (nextChoices, { dismissed = false } = {}) => {
      const next = writeConsentState({
        version: 2,
        decided: true,
        dismissed,
        decidedAt: new Date().toISOString(),
        choices: toChoiceMap(nextChoices),
      });
      setState(next);
      setOpen(false);
      applyConsentSideEffects(next);
      emitConsentChanged(next);
      advanceFirstUseAfterCookieDecision();
    },
    []
  );

  const setChoice = (key, value) => {
    if (key === "strictly") return;
    setState((prev) => ({
      ...prev,
      choices: toChoiceMap({
        ...(prev?.choices || DEFAULT_CONSENT),
        [key]: !!value,
      }),
    }));
  };

  const closeDialog = () => {
    // Closing is treated as a decision with current toggles (defaults are optional=false).
    persistDecision(prefs, { dismissed: true });
  };

  const saveChoices = () => persistDecision(prefs, { dismissed: false });

  const rejectOptional = () =>
    persistDecision({ ...DEFAULT_CONSENT }, { dismissed: false });

  const acceptAll = () => {
    const all = Object.fromEntries(
      CONSENT_CATEGORIES.map((key) => [key, true])
    );
    all.strictly = true;
    persistDecision(all, { dismissed: false });
  };

  React.useEffect(() => {
    window.afrimedCookies = {
      open: () => setOpen(true),
      reset: () => {
        clearConsentState();
        const fresh = readConsentState();
        setState(fresh);
        setExpanded(buildExpandedMap());
        setOpen(true);
        applyConsentSideEffects(fresh);
        emitConsentChanged(fresh);
      },
      get: () => readConsentState(),
      has: (key) => hasConsent(key),
    };

    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("consent") === "reset") {
        window.afrimedCookies.reset();
      }
    } catch {
      // ignore query parse errors
    }

    return () => {
      try {
        delete window.afrimedCookies;
      } catch {
        // ignore cleanup errors
      }
    };
  }, []);

  if (!open) return null;

  return (
    <div className="auth-bg fixed inset-0 z-[1000] grid place-items-center p-3 sm:p-4">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[rgba(10,14,22,0.30)] backdrop-blur-[2px]"
      />
      <div
        className="relative w-full max-w-[720px] max-h-[calc(100dvh-24px)] sm:max-h-[calc(100dvh-40px)] rounded-[24px] flex flex-col overflow-hidden"
        style={{
          background: "rgba(var(--afrimed-auth-panel), 0.96)",
          border: "1px solid rgba(var(--afrimed-auth-panel-border), 0.55)",
          boxShadow: "var(--afrimed-auth-panel-shadow)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
        }}
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="relative px-5 pt-5 text-left">
            <button
              type="button"
              aria-label={t("common.cookies.close", "Close cookie preferences")}
              onClick={closeDialog}
              className="absolute right-4 top-4 h-9 w-9 rounded-full grid place-items-center text-on-surface-var hover:bg-[rgba(var(--clr-on-surface),0.06)]"
            >
              <span className="text-xl leading-none">×</span>
            </button>

            <img
              src="/afrimed_elephant_icon.svg"
              alt="AfriMed Icon"
              className="absolute right-14 top-5 h-10 w-10 opacity-90"
            />

            <h2 className="text-title-large text-on-surface pr-20">
              {t("common.cookies.title", "AfriMed™ Cookie Preferences")}
            </h2>
            <p className="text-body-medium text-on-surface-var mt-1 mb-2 pr-20">
              {t(
                "common.cookies.intro",
                "Choose how optional cookies are used. Optional categories are off by default. Manage cookies here below.",
              )}
            </p>
            <p className="text-body-small text-on-surface-var mb-4 pr-2">
              {t(
                "common.cookies.support",
                "Designed to support GDPR consent standards and African Union data protection principles for transparency, purpose limitation, and user control.",
              )}
            </p>
          </div>

          <div className="h-px mx-5 bg-[rgba(var(--afrimed-auth-panel-border),0.5)]" />

          <div className="px-2 sm:px-4 py-2">
            {ORDER.map(({ key, labelKey, locked, bodyKey }) => {
              const isOpen = !!expanded[key];
              const label = t(`common.cookies.categories.${labelKey}.label`, labelKey);
              const body = t(`common.cookies.categories.${labelKey}.body`, bodyKey);
              return (
                <div key={key} className="px-3 py-2">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 text-body-large text-on-surface">{label}</div>

                    {locked ? (
                      <span className="text-body-small text-on-surface-var whitespace-nowrap">
                        {t("common.cookies.alwaysActive", "Always active")}
                      </span>
                    ) : (
                      <Switch
                        ariaLabel={`Toggle ${label}`}
                        checked={!!prefs[key]}
                        onChange={(v) => setChoice(key, v)}
                      />
                    )}

                    <button
                      type="button"
                      aria-label={
                        isOpen ? `Hide details for ${label}` : `Show details for ${label}`
                      }
                      onClick={() =>
                        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
                      }
                      className="ml-1 h-8 w-8 rounded-full grid place-items-center text-on-surface-var hover:bg-[rgba(var(--clr-on-surface),0.05)]"
                    >
                      <span className="text-xl leading-none select-none">
                        {isOpen ? "×" : "+"}
                      </span>
                    </button>
                  </div>

                  <div
                    className="overflow-hidden transition-[max-height,opacity] duration-200"
                    style={{ maxHeight: isOpen ? 220 : 0, opacity: isOpen ? 1 : 0 }}
                  >
                    <p className="text-body-medium text-on-surface-var mt-2 pr-2">{body}</p>
                  </div>

                  <div className="h-px mt-3 bg-[rgba(var(--afrimed-auth-panel-border),0.42)]" />
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="px-4 sm:px-5 pb-4 pt-3 border-t"
          style={{
            borderColor: "rgba(var(--afrimed-auth-panel-border), 0.5)",
            background: "rgba(var(--afrimed-auth-panel-muted), 0.92)",
          }}
        >
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              className="w-full sm:w-auto min-h-[44px] px-4 py-2 rounded-xl text-label-large font-medium text-on-surface"
              style={{
                background: "rgba(var(--clr-on-surface), 0.06)",
              }}
              onClick={rejectOptional}
            >
              {t("common.cookies.rejectOptional", "Reject optional")}
            </button>
            <button
              type="button"
              className="w-full sm:w-auto min-h-[44px] px-4 py-2 rounded-xl text-label-large font-medium text-on-surface"
              style={{
                background: "rgba(var(--clr-on-surface), 0.08)",
              }}
              onClick={saveChoices}
            >
              {t("common.cookies.saveChoices", "Save choices")}
            </button>
            <button
              type="button"
              className="w-full sm:w-auto min-h-[44px] px-4 py-2 rounded-xl text-label-large font-medium bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))]"
              onClick={acceptAll}
            >
              {t("common.cookies.acceptAll", "Accept all")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
